/* eslint-disable @typescript-eslint/no-explicit-any -- lineup slot payloads from API are loosely typed */
/**
 * Maps POST /lineup/potential-lineups batter slots → ProjectedLineup row props (ba / oba / slg).
 *
 * IMPORTANT: Keep this file identical to:
 *   dominus-baseball-admin/src/utils/projectedLineupUtils.ts
 * The API sends ytd_ba, ytd_oba, ytd_slg (and nested rwPlayer), not ytdAvg / batting_avg.
 */

export interface ProjectedLineupMappedRow {
  name?: string;
  position?: string;
  ba?: number;
  oba?: number;
  slg?: number;
  first_name?: string;
  last_name?: string;
  image?: string;
}

export type PotentialLineupPayload = {
  awayLineup?: (Record<string, unknown> | null)[] | null;
  homeLineup?: (Record<string, unknown> | null)[] | null;
  awayStarter?: unknown;
  homeStarter?: unknown;
};

export function slotToRow(slot: Record<string, unknown> | null): ProjectedLineupMappedRow {
  if (!slot || typeof slot !== 'object') return {};
  const rw = (slot as any).rwPlayer as Record<string, unknown> | undefined;
  const p = rw ?? (slot as Record<string, unknown>);

  const first = (p.first_name as string) ?? ((slot as any).first_name as string);
  const last = (p.last_name as string) ?? ((slot as any).last_name as string);
  const name =
    last && first
      ? `${last}, ${(first as string)[0]}.`
      : ((slot as any).name as string) ?? (p.name as string) ?? '—';

  const position = ((slot as any).position as string) ?? (p.position as string);

  const ytdHits = Number((slot as any).ytd_hits);
  const ytdAb = Number((slot as any).ytd_ab);
  const ytdBb = Number((slot as any).ytd_bb);
  const ytdHbp = Number((slot as any).ytd_hbp);
  const ytdSf = Number((slot as any).ytd_sf);
  const ytdTb = Number((slot as any).ytd_tb);

  const ba =
    typeof (slot as any).ytd_ba === 'number'
      ? ((slot as any).ytd_ba as number)
      : Number.isFinite(ytdHits) && Number.isFinite(ytdAb) && ytdAb > 0
        ? ytdHits / ytdAb
        : undefined;
  const obaDen = ytdAb + ytdBb + ytdHbp + ytdSf;
  const oba =
    typeof (slot as any).ytd_oba === 'number'
      ? ((slot as any).ytd_oba as number)
      : Number.isFinite(obaDen) && obaDen > 0
        ? (ytdHits + ytdBb + ytdHbp) / obaDen
        : undefined;
  const slg =
    typeof (slot as any).ytd_slg === 'number'
      ? ((slot as any).ytd_slg as number)
      : Number.isFinite(ytdTb) && Number.isFinite(ytdAb) && ytdAb > 0
        ? ytdTb / ytdAb
        : undefined;

  const image =
    (rw?.image as string) ??
    (p.image as string) ??
    ((slot as any).image as string);

  return { name, position, ba, oba, slg, first_name: first, last_name: last, image };
}

export function starterToString(starter: unknown): string {
  if (starter == null) return '';
  const s = starter as Record<string, unknown>;
  const p = (s.rwPlayer as Record<string, unknown>) ?? s;
  const first = (p.first_name as string) ?? (s.first_name as string);
  const last = (p.last_name as string) ?? (s.last_name as string);
  if (last && first) return `${last}, ${(first as string)[0]}.`;
  return (s.name as string) ?? (p.name as string) ?? '';
}

/** Shared mapping used by ProjectedLineupModal and dashboard panels. */
export function buildLineupsFromPotentialResponse(data: PotentialLineupPayload | null | undefined): {
  awayLineup: ProjectedLineupMappedRow[];
  homeLineup: ProjectedLineupMappedRow[];
  awayStarter: string;
  homeStarter: string;
} {
  const awayLineup = (data?.awayLineup ?? Array(9).fill(null)).map((s) =>
    slotToRow(s as Record<string, unknown> | null)
  );
  const homeLineup = (data?.homeLineup ?? Array(9).fill(null)).map((s) =>
    slotToRow(s as Record<string, unknown> | null)
  );
  const awayStarter = data ? starterToString(data.awayStarter) : '';
  const homeStarter = data ? starterToString(data.homeStarter) : '';
  return { awayLineup, homeLineup, awayStarter, homeStarter };
}
